import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

/**
 * Live IST clock. Updates every second. Uses Intl with Asia/Kolkata timezone
 * so it stays correct regardless of the viewer's system time zone.
 *
 * On first render before mount, we show a `--:--:--` placeholder so SSR/CSR
 * markup match and hydration is stable (not that we do SSR — but keeps
 * initial paint stable).
 */
export function IstClock({ compact = false }: { compact?: boolean }) {
  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const time =
    now &&
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      second: compact ? undefined : '2-digit',
      hour12: false,
    }).format(now);

  const date =
    now &&
    new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
    }).format(now);

  return (
    <div
      className={
        'inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 font-mono text-xs ' +
        (compact ? '' : '')
      }
      title={now ? `${date} · Indian Standard Time` : 'Indian Standard Time'}
    >
      <Clock className="h-3 w-3 text-muted-foreground" />
      <span className="tabular font-semibold text-foreground">
        {time ?? (compact ? '--:--' : '--:--:--')}
      </span>
      <span className="text-[9px] font-semibold tracking-wide text-muted-foreground">IST</span>
    </div>
  );
}
